// Utility functions for storing and applying price overrides
// Overrides are loaded once at app start and applied across all data modules
import { appendCustomItems } from './menuContent';

const PRICE_KEYS = new Set([
  'price',
  'base_price',
  'basePrice',
  'price_adjustment',
  'priceAdjustment',
  'combo_price',
]);

const NAME_KEYS = ['name', 'title', 'label'];

export const AVAILABILITY_PREFIX = 'availability.';

let cachedOverrides = {};

export const setPriceOverrides = (overrides) => {
  cachedOverrides = overrides && typeof overrides === 'object' ? overrides : {};
};

export const getPriceOverrides = () => cachedOverrides;

const cloneDeep = (value) => JSON.parse(JSON.stringify(value));

const encodePathSegment = (value) => encodeURIComponent(String(value));

const getArrayItemPath = (pathPrefix, item, index) => {
  if (item && typeof item === 'object' && item.price_override_key) {
    return `${pathPrefix}.items.${encodePathSegment(item.price_override_key)}`;
  }

  return `${pathPrefix}.${index}`;
};

const coerceOverrideValue = (originalValue, overrideValue) => {
  if (overrideValue === '') {
    return '';
  }

  if (typeof originalValue === 'number') {
    const numeric = Number(overrideValue);
    return Number.isNaN(numeric) ? originalValue : numeric;
  }

  return overrideValue;
};

const applyOverridesToValue = (value, path, overrides) => {
  const overrideValue = overrides[path];
  if (overrideValue === undefined) {
    return value;
  }

  return coerceOverrideValue(value, overrideValue);
};

const walkAndApply = (node, pathPrefix, overrides) => {
  if (Array.isArray(node)) {
    return node.map((item, index) =>
      walkAndApply(item, getArrayItemPath(pathPrefix, item, index), overrides)
    );
  }

  if (node && typeof node === 'object') {
    const next = { ...node };
    Object.entries(next).forEach(([key, value]) => {
      const path = `${pathPrefix}.${key}`;
      if (PRICE_KEYS.has(key)) {
        next[key] = applyOverridesToValue(value, path, overrides);
      } else {
        next[key] = walkAndApply(value, path, overrides);
      }
    });
    return next;
  }

  return node;
};

export const applyOverridesToData = (moduleKey, data, overrides = cachedOverrides) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const clone = cloneDeep(appendCustomItems(moduleKey, data, overrides));
  return walkAndApply(clone, moduleKey, overrides);
};

const getItemId = (itemOrId) => {
  if (itemOrId && typeof itemOrId === 'object') {
    return itemOrId.id;
  }

  return itemOrId;
};

export const getAvailabilityKey = (moduleKey, itemOrId) => {
  const itemId = getItemId(itemOrId);
  if (!moduleKey || itemId === undefined || itemId === null || itemId === '') {
    return null;
  }

  return `${AVAILABILITY_PREFIX}${moduleKey}.${encodePathSegment(itemId)}`;
};

const isUnavailableOverride = (value) => (
  value === false ||
  value === 0 ||
  (typeof value === 'string' && ['false', '0', 'off', 'unavailable'].includes(value.trim().toLowerCase()))
);

// Availability is intentionally sparse: a missing key means available. Only an
// explicit false-like value hides an item, which keeps all existing catalog
// items visible until an administrator chooses otherwise.
export const isItemAvailable = (moduleKey, itemOrId, overrides = cachedOverrides) => {
  const key = getAvailabilityKey(moduleKey, itemOrId);
  if (!key) return true;

  return !isUnavailableOverride(overrides?.[key]);
};

// Call this after applyOverridesToData. Annotating without reordering keeps the
// legacy array-index price paths aligned with their original catalog entries.
export const applyAvailabilityToData = (moduleKey, data, overrides = cachedOverrides) => {
  const annotate = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    return {
      ...item,
      is_available: isItemAvailable(moduleKey, item, overrides),
    };
  };

  return Array.isArray(data) ? data.map(annotate) : annotate(data);
};

export const filterAvailableItems = (moduleKey, items, overrides = cachedOverrides) => {
  if (!Array.isArray(items)) return [];

  return applyAvailabilityToData(moduleKey, items, overrides)
    .filter((item) => item?.is_available !== false);
};

export const getAvailabilityOverrides = (overrides = cachedOverrides) =>
  Object.fromEntries(
    Object.entries(overrides || {}).filter(([key]) => key.startsWith(AVAILABILITY_PREFIX))
  );

const formatModuleLabel = (moduleKey) =>
  moduleKey
    .replace(/([A-Z])/g, ' $1')
    .replace(/\./g, ' / ')
    .replace(/_/g, ' ')
    .replace(/^./, (char) => char.toUpperCase());

const formatPriceLabel = (key) => {
  switch (key) {
    case 'base_price':
    case 'basePrice':
      return 'Base Price';
    case 'price_adjustment':
    case 'priceAdjustment':
      return 'Price Adjustment';
    default:
      return 'Price';
  }
};

const pickName = (node) => {
  if (!node || typeof node !== 'object') {
    return null;
  }
  for (const key of NAME_KEYS) {
    if (typeof node[key] === 'string' && node[key].trim()) {
      return node[key].trim();
    }
  }
  return null;
};

const walkAndCollect = (node, pathPrefix, overrides, labels, results, moduleLabel) => {
  if (Array.isArray(node)) {
    node.forEach((item, index) => {
      walkAndCollect(
        item,
        getArrayItemPath(pathPrefix, item, index),
        overrides,
        labels,
        results,
        moduleLabel
      );
    });
    return;
  }

  if (node && typeof node === 'object') {
    const nameValue = pickName(node);
    const nextLabels = nameValue ? [...labels, nameValue] : labels;

    Object.entries(node).forEach(([key, value]) => {
      const path = `${pathPrefix}.${key}`;
      if (PRICE_KEYS.has(key)) {
        const label = nextLabels.length
          ? `${nextLabels.join(' / ')} - ${formatPriceLabel(key)}`
          : `${moduleLabel} - ${formatPriceLabel(key)}`;
        results.push({
          id: path,
          path,
          label,
          category: moduleLabel,
          currentValue: value,
          overrideValue: overrides[path],
        });
      } else {
        walkAndCollect(value, path, overrides, nextLabels, results, moduleLabel);
      }
    });
  }
};

export const collectPriceFields = (moduleKey, data, overrides = cachedOverrides) => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const results = [];
  const moduleLabel = formatModuleLabel(moduleKey);
  walkAndCollect(data, moduleKey, overrides, [], results, moduleLabel);
  return results;
};

const isAvailabilityItem = (node) => (
  node &&
  typeof node === 'object' &&
  !Array.isArray(node) &&
  node.id !== undefined &&
  node.id !== null &&
  Boolean(pickName(node))
);

const walkAndCollectAvailability = (node, scope, overrides, results, seen) => {
  if (Array.isArray(node)) {
    node.forEach((item) => {
      if (isAvailabilityItem(item)) {
        const path = getAvailabilityKey(scope, item);
        if (path && !seen.has(path)) {
          seen.add(path);
          results.push({
            id: path,
            path,
            moduleKey: scope,
            itemId: item.id,
            label: pickName(item),
            category: formatModuleLabel(scope),
            isAvailable: isItemAvailable(scope, item, overrides),
          });
        }
      }
    });
    return;
  }

  if (!node || typeof node !== 'object') return;

  if (isAvailabilityItem(node)) {
    const path = getAvailabilityKey(scope, node);
    if (path && !seen.has(path)) {
      seen.add(path);
      results.push({
        id: path,
        path,
        moduleKey: scope,
        itemId: node.id,
        label: pickName(node),
        category: formatModuleLabel(scope),
        isAvailable: isItemAvailable(scope, node, overrides),
      });
    }
    return;
  }

  Object.entries(node).forEach(([key, value]) => {
    if (value && typeof value === 'object') {
      walkAndCollectAvailability(value, `${scope}.${key}`, overrides, results, seen);
    }
  });
};

export const collectAvailabilityFields = (moduleKey, data, overrides = cachedOverrides) => {
  if (!data || typeof data !== 'object') return [];

  const results = [];
  walkAndCollectAvailability(data, moduleKey, overrides, results, new Set());
  return results;
};
